import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, Building2, Power } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { InvoiceCompanyForm, InvoiceCompany } from './InvoiceCompanyForm';

export function InvoiceCompaniesPanel() {
  const qc = useQueryClient();
  const [items, setItems] = useState<InvoiceCompany[]>([]);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'invoice_companies'],
    queryFn: async () => {
      try { return (await api.get<{ items?: InvoiceCompany[] }>('/settings/invoice_companies')).data ?? null; }
      catch { return null; }
    },
  });

  useEffect(() => {
    if (data && Array.isArray((data as any).items)) setItems((data as any).items);
    else if (data === null) setItems([]);
  }, [data]);

  const save = useMutation({
    mutationFn: async (next: InvoiceCompany[]) => (await api.put('/settings/invoice_companies', { items: next })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings', 'invoice_companies'] }); toast.success('Invoice companies saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const remove = (idx: number) => {
    if (!confirm(`Remove ${items[idx].company_name} (${items[idx].company_gstin})?`)) return;
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
    save.mutate(next);
  };

  const toggleStatus = (idx: number) => {
    const next = items.map((c, i) => i === idx ? { ...c, isActive: c.isActive === false ? true : false } : c);
    setItems(next);
    save.mutate(next);
  };

  const onUpsert = (rec: InvoiceCompany) => {
    let next: InvoiceCompany[];
    const existing = items.findIndex(c => c.company_gstin === rec.company_gstin);
    if (existing >= 0) next = items.map((c, i) => i === existing ? rec : c);
    else next = [...items, rec];
    setItems(next);
    save.mutate(next);
    setEditingIdx(null);
    setShowCreate(false);
  };

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">GST Details / Billing Companies</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            One entry per GSTIN you bill from — used in invoice dropdowns. Each entry stores legal name, PAN, HSN, GST rate, address, bank details and invoice terms. Inactive entries are hidden from booking dropdowns.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="px-3 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700 flex items-center gap-2">
          <Plus size={14} /> Add GST Detail
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-8 text-center text-sm text-slate-500">
          <Building2 size={24} className="mx-auto mb-2 opacity-40" />
          No GST details configured yet. Click <strong>Add GST Detail</strong> to add your first invoicing entity.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Logo</th>
                <th className="text-left px-3 py-2">GSTIN</th>
                <th className="text-left px-3 py-2">Company</th>
                <th className="text-left px-3 py-2">PAN</th>
                <th className="text-left px-3 py-2">State</th>
                <th className="text-left px-3 py-2">HSN / SAC</th>
                <th className="text-left px-3 py-2">GST Rate</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="px-3 py-2 w-28" />
              </tr>
            </thead>
            <tbody>
              {items.map((c, idx) => (
                <tr key={c.company_gstin + idx} className="border-t border-slate-100">
                  <td className="px-3 py-2">{(c as any).logo_url ? <img src={(c as any).logo_url} alt="logo" style={{ height: 32, maxWidth: 56 }} /> : <span className="text-xs text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 font-mono text-xs">{c.company_gstin}</td>
                  <td className="px-3 py-2 font-medium">{c.company_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{c.PAN}</td>
                  <td className="px-3 py-2 text-slate-600">{c.STATE}</td>
                  <td className="px-3 py-2 font-mono text-xs">{c.HSN} / {c.SAC_CODE}</td>
                  <td className="px-3 py-2">{c.gst_rate}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${c.isActive === false ? 'text-slate-400' : 'text-success'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${c.isActive === false ? 'bg-slate-300' : 'bg-success'}`} />
                      {c.isActive === false ? 'Inactive' : 'Active'}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button onClick={() => setEditingIdx(idx)} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded" title="Edit"><Edit2 size={14} /></button>
                    <button onClick={() => toggleStatus(idx)}
                      className={`p-1.5 rounded ${c.isActive === false ? 'text-success hover:bg-success/10' : 'text-amber-600 hover:bg-amber-50'}`}
                      title={c.isActive === false ? 'Activate' : 'Deactivate'}><Power size={14} /></button>
                    <button onClick={() => remove(idx)} className="p-1.5 text-slate-400 hover:text-danger hover:bg-danger/10 rounded" title="Delete"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add GST Detail" subtitle="Per-GSTIN invoice profile" maxWidth="4xl">
        <InvoiceCompanyForm onSubmit={onUpsert} onCancel={() => setShowCreate(false)} />
      </Modal>

      <Modal isOpen={editingIdx !== null} onClose={() => setEditingIdx(null)}
        title="Edit GST Detail"
        subtitle={editingIdx !== null ? `${items[editingIdx]?.company_name} (${items[editingIdx]?.company_gstin})` : undefined}
        maxWidth="4xl">
        {editingIdx !== null && (
          <InvoiceCompanyForm defaultValues={items[editingIdx]} onSubmit={onUpsert} onCancel={() => setEditingIdx(null)} />
        )}
      </Modal>
    </div>
  );
}
